import { motion } from "framer-motion";
import { Container } from "@/components/ui/Container";
import { SectionHeading } from "@/components/ui/SectionHeading";
import { staggerContainer, fadeInUp } from "@/lib/motion";
import { Code, DatabaseScript, Globe } from "iconoir-react";
import { useLanguage } from "@/components/LanguageContext";

// Products defined inside Component for translation context

export function SoftwareSection() {
  const { t } = useLanguage();

  const products = [
    {
      icon: Code,
      title: t("Traffic Counter AI", "Traffic Counter AI"),
      desc: t("Détection de véhicules en temps réel, classification et matrice OD par vision par ordinateur.", "Real-time vehicle detection, classification, and O-D matrix analytics powered by computer vision."),
      tags: ["YOLO", "Edge AI", t("Temps réel", "Real-time")],
    },
    {
      icon: Globe,
      title: t("Simulateur ZFE", "ZFE Simulator"),
      desc: t("Modélisation de Zones à Faibles Émissions avec calcul des émissions et impact des politiques publiques.", "Low-Emission Zone scenario modeling with emissions calculation, GIS visualization, and policy impact analysis."),
      tags: [t("Simulation", "Simulation"), "GIS", "ESG"],
    },
    {
      icon: DatabaseScript,
      title: t("Plateforme Analytique SIG", "GIS Analytics Platform"),
      desc: t("Tableau de bord d'intelligence territoriale avec analyse spatiale, cartes de chaleur et diagnostics infra.", "Territorial intelligence dashboard with spatial analysis, heatmaps, and infrastructure diagnostics."),
      tags: [t("Cartographie", "Mapping"), t("Analytique", "Analytics"), t("Tableaux de bord", "Dashboards")],
    },
  ];

  return (
    <section className="relative py-24 lg:py-32 overflow-hidden flex items-center min-h-[80vh]">
      {/* Immersive Full Background Image */}
      <div className="absolute inset-0 z-0">
        <div 
          className="w-full h-full object-cover"
          style={{
            backgroundImage: "url('/images/Image_IA_Traffic2.png')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
      </div>

      {/* Heavy Dark Overlay for Readability */}
      <div className="absolute inset-0 z-0 bg-charcoal/85" />
      <div className="absolute inset-0 z-0 bg-gradient-to-t from-charcoal via-transparent to-charcoal" />

      <Container className="relative z-10 w-full">
        <div className="text-center max-w-3xl mx-auto mb-16 lg:mb-24">
          <SectionHeading
            overline={t("Solutions Digitales", "Digital Solutions")}
            title={t("L'Intelligence Numérique. Conçue pour l'Impact.", "Digital Intelligence. Built for Impact.")}
            subtitle={t("Outils propriétaires et plateformes conçus pour l'analyse de mobilité, la modélisation et l'intelligence territoriale.", "Proprietary tools and platforms designed for mobility analytics, emissions modeling, and territorial intelligence.")}
            light
          />
        </div>

        <motion.div
           variants={staggerContainer}
           initial="hidden"
           whileInView="visible"
           viewport={{ once: true, margin: "-80px" }}
           className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8"
        >
          {products.map((product) => (
             <motion.div
               key={product.title}
               variants={fadeInUp}
               className="group relative flex flex-col p-8 rounded-3xl bg-white/5 backdrop-blur-xl border border-white/10 hover:bg-white/10 hover:border-amber/40 transition-all duration-500 shadow-2xl"
             >
                {/* Glow effect on hover */}
                <div className="absolute -inset-px rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br from-amber/10 via-transparent to-transparent pointer-events-none" />
                
                <div className="relative z-10">
                  <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-charcoal/80 border border-white/10 text-amber mb-6 group-hover:bg-amber group-hover:text-charcoal transition-all duration-300 shadow-lg">
                    <product.icon width={28} height={28} strokeWidth={1.5} />
                  </div>
                  <h3 className="text-xl font-sans font-bold text-warm-white mb-3 group-hover:text-amber transition-colors duration-300">
                    {product.title}
                  </h3>
                  <p className="text-sm md:text-base text-warm-white/70 leading-relaxed mb-6 flex-grow">
                    {product.desc}
                  </p>
                  
                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mt-auto">
                    {product.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-xs px-3 py-1 rounded-full bg-charcoal/50 text-warm-white/60 border border-white/10 group-hover:border-amber/30 transition-colors"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
             </motion.div>
          ))}
        </motion.div>
      </Container>
    </section>
  );
}
